"use client";

import { useEffect } from "react";

/**
 * Авто-recovery от 404 на Next.js chunks после деплоя.
 *
 * Проблема: после `bash deploy.sh` имена CSS/JS chunks меняются. Браузер
 * с закэшированным старым HTML пытается загрузить старые chunks → 404 →
 * пользователь видит сломанную страницу (нет стилей, нет интерактивности).
 *
 * Решение: слушаем `error` events на `<link>`, `<script>` (и window-level
 * для `ChunkLoadError`). Если 404 идёт на `/_next/static/*` — делаем
 * one-shot reload, который подгрузит свежий HTML с актуальными chunks.
 *
 * Защита от infinite loop: sessionStorage flag. Если reload уже произошёл
 * за эту сессию — больше не пытаемся. Юзер увидит ошибку (логируется в
 * Sentry/GlitchTip через captureError), но страница не зациклится.
 *
 * Не влияет на SEO: клиент-сайд handler, краулеры его не триггерят.
 */
const RELOAD_FLAG = "tlr_chunk_reload_v1";

export function ChunkErrorRecovery() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    function isChunkUrl(url: string | null): boolean {
      if (!url) return false;
      return url.includes("/_next/static/");
    }

    function tryReload(reason: string): void {
      // Защита от infinite reload: throttle 10 секунд между попытками.
      // Хранится как timestamp последнего reload в sessionStorage.
      // Если ошибка повторяется через >10с — пробуем ещё один reload
      // (вдруг это временная проблема, например deploy в момент загрузки).
      // Если ошибка постоянная (extension, bug) — юзер видит её один раз
      // в 10с, всё равно может пользоваться сайтом.
      let lastReloadTs = 0;
      try {
        const raw = sessionStorage.getItem(RELOAD_FLAG);
        lastReloadTs = raw ? Number(raw) : 0;
      } catch {
        // sessionStorage недоступен (privacy mode) — пропускаем
        return;
      }
      const now = Date.now();
      if (lastReloadTs > 0 && now - lastReloadTs < 10_000) {
        // Меньше 10с с последнего reload — это infinite loop, останавливаемся.
        console.warn(
          `[ChunkErrorRecovery] reload throttled (${reason}) — last reload ${Math.round(
            (now - lastReloadTs) / 1000,
          )}s ago`,
        );
        return;
      }
      try {
        sessionStorage.setItem(RELOAD_FLAG, String(now));
      } catch {
        // ignore
      }
      console.info(`[ChunkErrorRecovery] reloading page (${reason})`);
      window.location.reload();
    }

    // 1) Listener на resource errors (загрузка <link>, <script>, <img>)
    //    Capture phase (true), потому что error events не bubble.
    function onResourceError(e: Event): void {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const tag = target.tagName?.toLowerCase();
      if (tag !== "link" && tag !== "script") return;

      const url =
        (target as HTMLLinkElement).href ?? (target as HTMLScriptElement).src ?? null;
      if (!isChunkUrl(url)) return;

      tryReload(`resource 404: ${url}`);
    }

    // 2) Listener на ChunkLoadError и hydration mismatches.
    //    React Error #418 = "Text content does not match server-rendered HTML".
    //    Это случается когда browser cache держит старый HTML, но JS уже от
    //    новой сборки (имена chunks разные). Без auto-reload пользователь
    //    видит "Application error" overlay и не понимает что делать.
    function onWindowError(e: ErrorEvent): void {
      const msg = e.message ?? "";
      const isChunkError =
        msg.includes("ChunkLoadError") ||
        msg.includes("Loading chunk") ||
        msg.includes("Loading CSS chunk");
      // React hydration errors которые ТОЧНО связаны со stale chunks/HTML:
      // - #421 (HTML structure mismatch) — реально означает что server отдал
      //   старую разметку под новые chunks. Reload решает.
      // - #422 (suspense boundary differ), #423 (component name mismatch),
      //   #425 (text differs AFTER hydration — runtime drift) — аналогично.
      //
      // HOTFIX-4: НЕ ловим #418 (text content mismatch) — этот код часто
      // фейерится от браузерных расширений (Bybit/MetaMask/Grammarly инжектят
      // в DOM перед hydration) и от любых тонких различий в text rendering.
      // Reload не помогает — после reload расширение снова инжектит → loop.
      // Реальные #418 в коде ловим через GlitchTip и фиксим точечно (RULE-HYDRATION-02).
      const isHydrationError =
        msg.includes("Minified React error #421") ||
        msg.includes("Minified React error #422") ||
        msg.includes("Minified React error #423") ||
        msg.includes("Minified React error #425") ||
        msg.includes("Hydration failed");
      if (!isChunkError && !isHydrationError) return;
      tryReload(`window error: ${msg.slice(0, 80)}`);
    }

    // 3) Listener на unhandledrejection (когда промис из dynamic import падает)
    function onUnhandledRejection(e: PromiseRejectionEvent): void {
      const reason = e.reason;
      const msg =
        typeof reason === "string"
          ? reason
          : (reason as Error | undefined)?.message ?? "";
      const name = (reason as Error | undefined)?.name ?? "";
      const isChunkError =
        name === "ChunkLoadError" ||
        msg.includes("ChunkLoadError") ||
        msg.includes("Loading chunk") ||
        msg.includes("Loading CSS chunk");
      // HOTFIX-4: убрали #418 из триггеров — см. onWindowError выше.
      const isHydrationError = msg.includes("Hydration failed");
      if (!isChunkError && !isHydrationError) return;
      tryReload(`unhandled rejection: ${msg.slice(0, 80)}`);
    }

    window.addEventListener("error", onResourceError, true);
    window.addEventListener("error", onWindowError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("error", onResourceError, true);
      window.removeEventListener("error", onWindowError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return null;
}
